import { ApiError, apiFetch, parseErrorDetail } from "@/api/fetch";
import type {
  ClaimEvidenceResponse,
  ClaimSummaryRequest,
  ClaimSupportRequest,
  ClaimSupportView,
  Evidence,
  EvidenceItem,
  EvidenceStats,
} from "../types";

/** "A; B", or "A et al." when the list is truncated: the form citations and summaries parse. */
const authorLine = (names: string[], truncated: boolean): string | null =>
  names.length ? `${names.join("; ")}${truncated ? " et al." : ""}` : null;

const toItem = (ev: Evidence, side: "supports" | "refutes"): EvidenceItem => {
  const p = ev.paper;
  const snippet = ev.context ?? ev.evidence ?? "";
  const [start, end] = ev.evidence_offset ?? [null, null];
  return {
    snippet,
    doc_id: p.id,
    source: p.source,
    section: ev.section,
    pub_year: p.year,
    pub_date: p.date,
    supports_score: side === "supports" ? ev.confidence : null,
    refutes_score: side === "refutes" ? ev.confidence : null,
    evidence: ev.evidence,
    evidence_start: start,
    evidence_end: end,
    title: p.title,
    journal: p.journal,
    authors: authorLine(p.authors, p.authors_truncated),
    doi: p.doi,
    pmid: p.pmid,
    citations: p.citations,
    author_h_index: p.author_h_index,
    retracted: p.retracted,
    expression_of_concern: p.expression_of_concern,
    retraction_doi: p.retraction_doi,
    retraction_notice: p.retraction_notice,
    design: p.design,
    evidence_weight: ev.weight,
    figures: ev.figures ?? undefined,
  };
};

/** The message of a v1 error body ({"error": {"message": ...}}). */
export const v1ErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body = (await response.clone().json()) as { error?: { message?: unknown } };
    if (typeof body?.error?.message === "string") return body.error.message;
  } catch {
    /* fall through */
  }
  return parseErrorDetail(response);
};

/**
 * Logged out, the page runs the query through the anonymous route, which the server limits per
 * IP address and answers with 429 once the free queries are used. Page queries are logged
 * unless a signed-in user sets `noLog`; the anonymous route ignores it.
 */
export const huntClaim = async (
  body: ClaimSupportRequest,
  { anonymous = false, noLog = false }: { anonymous?: boolean; noLog?: boolean } = {},
): Promise<{ view: ClaimSupportView; permalink: string | null }> => {
  const init: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(noLog && !anonymous ? { "X-Evidence-No-Log": "1" } : {}),
    },
    body: JSON.stringify(body),
  };
  const response = anonymous
    ? await fetch("/api/v1/claims/support/anonymous", init)
    : await apiFetch("/v1/claims/support", init);
  if (!response.ok) {
    throw new ApiError(
      anonymous ? await v1ErrorMessage(response) : await parseErrorDetail(response),
      response.status,
    );
  }
  const data = (await response.json()) as ClaimEvidenceResponse | ClaimSupportView;
  return { view: toView(data), permalink: response.headers.get("X-Evidence-Permalink") };
};

const toView = (data: ClaimEvidenceResponse | ClaimSupportView): ClaimSupportView => {
  // The stashed open-question path returns the evidence service's own shape.
  if (!("object" in data) || data.object !== "claim_evidence") return data as ClaimSupportView;
  return {
    claim: data.claim,
    supports: data.supports.map((ev) => toItem(ev, "supports")),
    refutes: data.refutes.map((ev) => toItem(ev, "refutes")),
    ...(data.judge ? { judge: data.judge } : {}),
    timing_ms: { total: data.timing_ms.total },
  };
};

export interface EvidencePermalink {
  claim: string;
  params: Pick<ClaimSupportRequest, "top_n" | "min_conf" | "rules" | "judge">;
  view: ClaimSupportView;
  created_at: string;
}

/** A saved page result. Public, so it works logged out. */
export const fetchPermalink = async (id: string): Promise<EvidencePermalink> => {
  const response = await fetch(`/api/v1/claims/permalinks/${encodeURIComponent(id)}`);
  if (!response.ok) throw new ApiError(await v1ErrorMessage(response), response.status);
  const data = (await response.json()) as Omit<EvidencePermalink, "view"> & {
    result: ClaimEvidenceResponse | ClaimSupportView;
  };
  return { claim: data.claim, params: data.params, view: toView(data.result), created_at: data.created_at };
};

export interface ClaimFeedbackRequest {
  query: string;
  mode: "claim" | "question" | null;
  note?: string;
  result_counts?: { supporting?: number; refuting?: number; answers?: number };
}

/** Saves a query the user chose to submit as feedback, with an optional note. */
export const submitFeedback = async (body: ClaimFeedbackRequest): Promise<void> => {
  const response = await apiFetch("/v1/claims/feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
};

/** Counts one view of the page for the staff usage dashboard. Never throws. */
export const recordVisit = async (referrer: string | null, signedIn: boolean): Promise<void> => {
  const init: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ referrer }),
    keepalive: true,
  };
  try {
    await (signedIn ? apiFetch("/v1/claims/visit", init) : fetch("/api/v1/claims/visit", init));
  } catch {
    /* usage stats are best-effort */
  }
};

/** Attributes the signed-in user's just-created account to the page. */
export const recordSignup = async (): Promise<void> => {
  await apiFetch("/v1/claims/signup", { method: "POST", keepalive: true });
};

export const fetchEvidenceStats = async (
  days: number,
  includeInternal: boolean,
): Promise<EvidenceStats> => {
  const params = new URLSearchParams({ days: String(days), include_internal: String(includeInternal) });
  const response = await apiFetch(`/v1/claims/stats?${params}`);
  if (!response.ok) {
    throw new ApiError(await v1ErrorMessage(response), response.status);
  }
  return (await response.json()) as EvidenceStats;
};

/** Streams a cited summary; calls onText with each new piece of text. */
export const summarizeEvidence = async (
  body: ClaimSummaryRequest,
  onText: (text: string) => void,
  signal: AbortSignal,
): Promise<void> => {
  const response = await apiFetch("/v1/claims/summarize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok || !response.body) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    onText(decoder.decode(value, { stream: true }));
  }
};
